import { useTranslation } from "react-i18next";
import { useDelayFocus } from "../../../../hooks/use-delay-focus";
import { useCallback, useEffect, useRef, useState } from "react";
import { MicrophoneOptions, MicrophoneProps } from "./type";
import { useMicrophoneCheck } from "./hooks/use-microphone-check";
import { AudioVisualizer, LiveAudioVisualizer } from "react-audio-visualize";
import { WaveForm } from "./wave-form";
import { Button, Col, Divider, Flex, Row, Typography } from "antd";

import "./styles.less";
import PprDropdownSelect from "../../../shared/ppr-dropdown-select/ppr-dropdown-select";
import PprButton from "../../../shared/ppr-button/ppr-button";
import PprChipStatus from "../../../shared/ppr-chip-status/ppr-chip-status";

const { Title, Text } = Typography;

const MICROPHONE_PREFIX = "SYSTEM_CHECK_STEPPER.MICROPHONE";

const DEFAULT_MICROPHONE_OPTIONS: MicrophoneOptions = {
  silenceThreshold: 0.01,
};

export const Microphone = ({
  microphoneCheck,
  onComplete,
  onFail,
  microphoneOptions = DEFAULT_MICROPHONE_OPTIONS,
}: MicrophoneProps) => {
  const { t } = useTranslation("translation", { keyPrefix: MICROPHONE_PREFIX });
  const {
    loading,
    microphones,
    permissionState,
    microphoneStatus,
    selectedMic,
    setSelectedMic,
    handleStartRecording,
    setMicrophoneStatus,
    mediaStream,
    mediaRecorder,
    isRecording,
    audioBlob,
    countDown,
    hasSound,
  } = useMicrophoneCheck(microphoneOptions);
  const selectRef = useRef<HTMLInputElement>(null);
  useDelayFocus(
    selectRef,
    microphoneStatus !== "failed" && permissionState === "granted",
    28000,
  );
  const [recordAttempt, setRecordAttempt] = useState(0); 

  useEffect(() => {
    if (permissionState === "granted") {
      setMicrophoneStatus("not-started");
    } else if (permissionState === "denied") {
      setMicrophoneStatus("failed");
    }
  }, [permissionState]);

  useEffect(() => {
    const errors = [];
    let showFAQLink = false;
    onFail([]);

    if (microphoneStatus === "analyzing") return;

    if (microphoneStatus === "passed") {
      onComplete();
    } else {
      if (microphoneStatus === "failed") {
        errors.push(
          hasSound !== false ? `${MICROPHONE_PREFIX}.ERRORALERT` : `${MICROPHONE_PREFIX}.AUDIONOTCLEARERROR`,
        );
        if (hasSound === undefined) {
          showFAQLink = true;
        }
      }

      onFail(errors, "", showFAQLink);
    }
  }, [microphoneStatus, hasSound]);

  const handleStartRecordingClick = useCallback(() => {
    handleStartRecording(selectRef);
    setRecordAttempt(prev => prev + 1);
  }, [handleStartRecording]);

  return (
    microphoneCheck && (
      <Flex className="micro-check-container" vertical>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Flex vertical>
              <Title level={2}>{t(`MICROPHONESELECTION`)}</Title>
              <Text>{t(`DESCRIPTION`)}</Text>
              <div>
                <Text className="description-text">1.</Text>
                <Text className="description-text">{t(`INSTRUCTIONS.0`)}</Text>
              </div>
              <div>
                <Text className="description-text">2.</Text>
                <Text className="description-text">{t(`INSTRUCTIONS.1`)}</Text>
              </div>
              <div>
                <Text className="description-text">3.</Text>
                <Text className="description-text">{t(`INSTRUCTIONS.2`)}</Text>
              </div>
              <div>
                <Text className="description-text">4.</Text>
                <Text className="description-text">{t(`INSTRUCTIONS.3`)}</Text>
              </div>
            </Flex>
          </Col>
          <Col span={12}>
            <Flex vertical gap={16}>
              <PprDropdownSelect
                selectRef={selectRef}
                title={t(`LISTDESCRIPTION`)}
                options={microphones.map((e) => ({
                  value: e.deviceId,
                  label: e.label,
                }))}
                value={selectedMic?.deviceId ?? null}
                onChange={(e) =>
                  setSelectedMic(microphones.find((m) => m.deviceId === e))
                }
                placeholder="-"
                disabled={loading}
              />
              <Flex vertical gap={8}>
                <Title level={3} className="micro-check-title">
                  {t(`TITLEMICROPHONECHECK`)}
                </Title>
                {mediaStream && mediaRecorder && (
                  <LiveAudioVisualizer
                    mediaRecorder={mediaRecorder}
                    width={325}
                    height={75}
                    barWidth={3}
                    barColor="#1696FE"
                  />
                )}
                {!isRecording && hasSound && (
                  <AudioVisualizer
                    blob={audioBlob!}
                    width={325}
                    height={75}
                    barWidth={3}
                    gap={2}
                    barColor="#1696FE"
                  />
                )}
                {!isRecording && !hasSound && <WaveForm />}

                {((isRecording ||
                  hasSound ||
                  microphoneStatus !== "not-started") && permissionState === "granted") && (
                  <Flex justify={"center"}>
                    <Title
                      data-cy="recorder-result"
                      level={4}
                      className="micro-check-title"
                    >
                      {countDown === 0 ? t(`RECORDEDSTOPPED`) : countDown}
                    </Title>
                  </Flex>
                )}

                {!isRecording &&
                  ["not-started", "failed", "passed"].includes(microphoneStatus) && (
                    <Flex justify={"flex-start"}>
                      <PprButton
                        data-testid="start-recording-button"
                        onClick={handleStartRecordingClick}
                        disable={
                          !selectedMic ||
                          microphones.length === 0 ||
                          permissionState !== "granted" ||
                          loading
                        }
                        text={recordAttempt === 0 ? t(`STARTRECORDINGBUTTON`) : t(`RECORD_AGAIN_BUTTON`)}
                      />
                    </Flex>
                  )}
              </Flex>
            </Flex>
          </Col>
        </Row>

        <Divider className="custom-divider" />
        <Flex align="flex-start" justify="space-between">
          <Flex vertical flex={1}>
            <Title level={3}>{t(`AUDIOANALYSISTITLE`)}</Title>
            <Text>
              {t(
                `AUDIOANALYSISSTATUS.${["failed", "passed"].includes(microphoneStatus) ? "COMPLETED" : microphoneStatus.toUpperCase()}`,
              )}
            </Text>
          </Flex>
          <PprChipStatus status={microphoneStatus} data-testid="chip-status"/>
        </Flex>
      </Flex>
    )
  );
};

export default Microphone;
